import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import type { ColumnsType, PageRead } from '@components/table/types'
import { useDeleteScheduledTask } from '@services/cron/mutations/useDeleteScheduledTask'
import { useRunNow } from '@services/cron/mutations/useRunNow'
import { useUpdateScheduledTask } from '@services/cron/mutations/useUpdateScheduledTask'
import { useScheduledTasks } from '@services/cron/queries/useScheduledTasks'
import { Play, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'

import { statusKey } from '../status'
import type { ScheduledTask } from '../types'
import { CreateCronJobDialog } from './CreateCronJobDialog'

export function CronJobsTable() {
  const { t } = useTranslation('cron')
  const query = useScheduledTasks()
  const update = useUpdateScheduledTask()
  const runNow = useRunNow()
  const remove = useDeleteScheduledTask()
  const [createOpen, setCreateOpen] = useState(false)

  const columns: ColumnsType<ScheduledTask> = [
    {
      title: t('columns.task'),
      dataIndex: 'label',
      render: (label: string, record: ScheduledTask) => (
        <div>
          <div className="font-medium text-ink">{label}</div>
          <div className="text-xs text-ink/50">{record.task_key}</div>
        </div>
      )
    },
    {
      title: t('columns.schedule'),
      dataIndex: 'cron_expression',
      render: (value: string) => (
        <code className="rounded bg-ink/[0.06] px-2 py-0.5 text-xs">{value}</code>
      )
    },
    {
      title: t('columns.nextRun'),
      dataIndex: 'next_run_time',
      render: (value: string | null) =>
        value ? new Date(value).toLocaleString('sk-SK') : t('neverRun')
    },
    {
      title: t('columns.lastStatus'),
      dataIndex: 'last_status',
      render: (value: string | null) => {
        if (!value) return t('status.none')
        const key = statusKey(value)
        return key ? t(`statuses.${key}`) : value
      }
    },
    {
      title: t('columns.enabled'),
      dataIndex: 'enabled',
      align: 'center',
      render: (value: boolean, record: ScheduledTask) => (
        <span
          className="inline-flex"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            checked={value}
            disabled={update.isPending}
            onCheckedChange={(checked) =>
              update.mutate({ id: record.id, body: { enabled: checked } })
            }
          />
        </span>
      )
    },
    {
      title: t('columns.actions'),
      dataIndex: 'id',
      align: 'right',
      render: (_: number, record: ScheduledTask) => (
        <div
          className="flex justify-end gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="sm"
            variant="outline"
            disabled={!record.enabled || runNow.isPending}
            onClick={() => runNow.mutate(record.id)}
          >
            <Play className="h-3.5 w-3.5" />
            {t('actions.runNow')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => remove.mutate(record.id)}
            aria-label={t('actions.delete')}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )
    }
  ]

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('title')} />
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('actions.create')}
        </Button>
      </div>
      <Table<PageRead<ScheduledTask>>
        query={query}
        columns={columns}
        id="cron-jobs"
        hasSearch={false}
      />
      <CreateCronJobDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}
